import { useState, useEffect } from 'react'
import { router, usePage } from '@inertiajs/react'
import { useDebounce } from '../hooks/useDebounce'
import MemoryCard from './MemoryCard'
import Pagination from './Pagination'
import MarkdownEditor from './MarkdownEditor'

const MEMORY_TYPES = [
  "architecture", "bugfix", "decision", "discovery",
  "pattern", "config", "preference", "session_summary",
  "reference", "chore", "concept",
  // tech-debt: synthesis and session_context types exist in backend TYPES but are not listed here for filtering
]

const MEMORY_SCOPES = ["shared", "personal", "project"]

const SORT_OPTIONS = [
  { label: "Updated ↓", sort: "updated_at", dir: "desc" },
  { label: "Updated ↑", sort: "updated_at", dir: "asc"  },
  { label: "Created ↓", sort: "created_at", dir: "desc" },
  { label: "Created ↑", sort: "created_at", dir: "asc"  },
  { label: "Title A→Z", sort: "title",      dir: "asc"  },
  { label: "Title Z→A", sort: "title",      dir: "desc" },
]

const BLANK_FORM = { title: '', content: '', memory_type: 'decision', scope: 'shared', project_slug: '' }

export default function MemoryList({ memories, filters, active_session, projects, onSelect }) {
  const { auth } = usePage().props
  const isOwner = auth?.user?.role === 'owner'

  const [search, setSearch]           = useState(filters.q ?? "")
  const [typeFilter, setType]         = useState(filters.type ?? "")
  const [scopeFilter, setScopeFilter] = useState(filters.scope ?? "")
  const [archivedFilter, setArchived] = useState(!!filters.archived)
  const sortValue = `${filters.sort ?? "updated_at"}:${filters.dir ?? "desc"}`
  const [sortKey, setSortKey]         = useState(sortValue)
  const debouncedSearch               = useDebounce(search, 300)

  const [searchFocused, setSearchFocused] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newForm, setNewForm]         = useState({ ...BLANK_FORM, project_slug: filters.project ?? '' })
  const [creating, setCreating]       = useState(false)

  useEffect(() => {
    if (debouncedSearch === (filters.q ?? "")) return
    applyFilters({ q: debouncedSearch || undefined, page: 1 })
  }, [debouncedSearch])

  function handleArchivedToggle() {
    const next = !archivedFilter
    setArchived(next)
    applyFilters({ archived: next ? "true" : undefined, page: 1 })
  }

  function applyFilters(overrides) {
    const next = {
      ...(filters.project  ? { project:  filters.project  } : {}),
      ...(filters.q        ? { q:        filters.q         } : {}),
      ...(filters.type     ? { type:     filters.type      } : {}),
      ...(filters.scope    ? { scope:    filters.scope     } : {}),
      ...(filters.session  ? { session:  filters.session   } : {}),
      ...(filters.archived ? { archived: filters.archived  } : {}),
      ...(filters.sort && filters.sort !== "updated_at" ? { sort: filters.sort } : {}),
      ...(filters.dir  && filters.dir  !== "desc"       ? { dir:  filters.dir  } : {}),
      page: filters.page,
      ...overrides,
    }
    Object.keys(next).forEach(k => (next[k] === undefined || next[k] === "") && delete next[k])
    router.visit("/", { data: next, preserveState: true, preserveScroll: true })
  }

  function handleTypeChange(e) {
    const val = e.target.value
    setType(val)
    applyFilters({ type: val || undefined, page: 1 })
  }

  function handleScopeChange(e) {
    const val = e.target.value
    setScopeFilter(val)
    applyFilters({ scope: val || undefined, page: 1 })
  }

  function handleSortChange(e) {
    const val = e.target.value
    setSortKey(val)
    const [sort, dir] = val.split(":")
    const isDefault = sort === "updated_at" && dir === "desc"
    applyFilters({
      sort: isDefault ? undefined : sort,
      dir:  isDefault ? undefined : dir,
      page: 1,
    })
  }

  function handlePageChange(page) {
    applyFilters({ page })
  }

  function handleNewFormChange(e) {
    setNewForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleNewFormCancel() {
    setShowNewForm(false)
    setNewForm({ ...BLANK_FORM, project_slug: filters.project ?? '' })
  }

  function handleNewFormSubmit(e) {
    e.preventDefault()
    if (!newForm.project_slug) return
    setCreating(true)
    router.post('/memories', {
      title:        newForm.title,
      content:      newForm.content,
      memory_type:  newForm.memory_type,
      scope:        newForm.scope,
      project_slug: newForm.project_slug,
    }, {
      onSuccess: () => {
        setShowNewForm(false)
        setNewForm({ ...BLANK_FORM, project_slug: filters.project ?? '' })
        setCreating(false)
      },
      onError: () => {
        setCreating(false)
      },
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {active_session && (
        <div className="mb-3 px-3 py-2 rounded bg-violet-900/20 border border-violet-800/40 flex items-center justify-between">
          <div className="text-xs font-mono text-violet-300">
            <span className="text-zinc-500">session · </span>
            {active_session.developer}
            <span className="text-zinc-500"> · {active_session.project_name} · </span>
            {new Date(active_session.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {active_session.ended_at === null && <span className="ml-2 text-emerald-400">● open</span>}
          </div>
          <button
            onClick={() => router.visit('/')}
            className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            clear ×
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-40">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search memories…"
            className="w-full bg-zinc-800/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
          />
          {searchFocused && (
            <div className="absolute top-full left-0 mt-1 z-10 bg-zinc-800/90 border border-zinc-700 rounded-lg px-3 py-2.5 text-[10px] font-mono text-zinc-500 space-y-1 w-72 backdrop-blur-sm shadow-xl shadow-zinc-950/50">
              <p><span className="text-zinc-300">type:bugfix</span> filter by type</p>
              <p><span className="text-zinc-300">scope:personal</span> filter by scope</p>
              <p><span className="text-zinc-300">topic:praxis/</span> filter by topic prefix</p>
              <p className="text-zinc-500 pt-0.5">combine with free text: <span className="text-zinc-300">type:decision auth</span></p>
            </div>
          )}
        </div>
        <select
          value={typeFilter}
          onChange={handleTypeChange}
          className="bg-zinc-800/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
        >
          <option value="">All types</option>
          {MEMORY_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={scopeFilter}
          onChange={handleScopeChange}
          className="bg-zinc-800/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
        >
          <option value="">All scopes</option>
          {MEMORY_SCOPES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={sortKey}
          onChange={handleSortChange}
          className="bg-zinc-800/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
        >
          {SORT_OPTIONS.map(o => (
            <option key={`${o.sort}:${o.dir}`} value={`${o.sort}:${o.dir}`}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={handleArchivedToggle}
          className={`px-3 py-2 text-sm font-mono rounded-lg transition-colors shrink-0 ${
            archivedFilter
              ? "bg-zinc-700 text-zinc-100 border border-zinc-500"
              : "bg-zinc-800/70 text-zinc-500 border border-zinc-700 hover:text-zinc-300"
          }`}
        >
          archived
        </button>
        {isOwner && (
          <button
            onClick={() => setShowNewForm(open => !open)}
            className="px-3 py-1.5 text-sm font-mono bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 rounded-lg transition-colors shrink-0"
          >
            + New
          </button>
        )}
      </div>

      {/* Inline new-memory form */}
      {showNewForm && (
        <form
          onSubmit={handleNewFormSubmit}
          className="border border-zinc-700 rounded p-4 flex flex-col gap-3 bg-zinc-800/50"
        >
          <p className="text-xs font-mono text-zinc-500 uppercase tracking-widest">new memory</p>

          <input
            type="text"
            name="title"
            value={newForm.title}
            onChange={handleNewFormChange}
            placeholder="Title"
            required
            className="w-full bg-zinc-800/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
          />

          <div className="flex gap-3 flex-wrap">
            <select
              name="memory_type"
              value={newForm.memory_type}
              onChange={handleNewFormChange}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {MEMORY_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              name="scope"
              value={newForm.scope}
              onChange={handleNewFormChange}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {MEMORY_SCOPES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {!filters.project && (
              <select
                name="project_slug"
                value={newForm.project_slug}
                onChange={handleNewFormChange}
                required
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="">Select project…</option>
                {(projects ?? []).map(p => (
                  <option key={p.slug} value={p.slug}>{p.name}</option>
                ))}
              </select>
            )}
          </div>

          <MarkdownEditor
            name="content"
            value={newForm.content}
            onChange={handleNewFormChange}
            rows={4}
            compact={true}
            required
          />

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={creating}
              className="px-3 py-1.5 text-sm font-mono bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 rounded-lg transition-colors disabled:opacity-50"
            >
              {creating ? 'saving…' : 'save'}
            </button>
            <button
              type="button"
              onClick={handleNewFormCancel}
              className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              cancel
            </button>
          </div>
        </form>
      )}

      {/* Stats */}
      <p className="text-xs font-mono text-zinc-400">
        {memories.total_count} {memories.total_count === 1 ? "memory" : "memories"}
        {memories.total_pages > 1 && ` · page ${memories.current_page} of ${memories.total_pages}`}
      </p>

      {/* List */}
      {memories.data.length === 0 ? (
        <div className="border border-zinc-700 rounded-lg p-8 text-center font-mono text-sm text-zinc-500">
          No memories found.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {memories.data.map(m => <MemoryCard key={m.id} memory={m} query={filters.q} onSelect={onSelect} />)}
        </div>
      )}

      <Pagination
        current_page={memories.current_page}
        total_pages={memories.total_pages}
        onPageChange={handlePageChange}
      />
    </div>
  )
}
